import { useEffect, useState } from 'react'

/**
 * Hook that executes a function once when a condition is true.
 * The function will only be executed once, even if the condition or function changes in the future.
 */
export const useRunOnce = (fn: () => void, condition: boolean): boolean => {
  const [hasBeenCalled, setHasBeenCalled] = useState(false)

  useEffect(() => {
    if (condition && !hasBeenCalled) {
      setHasBeenCalled(true)
      fn()
    }
  }, [condition, fn, hasBeenCalled])

  return hasBeenCalled
}

/**
 * Hook that executes an asynchronous function once when a condition is true.
 * The function will only be executed once, even if the condition or function changes in the future.
 */
export const useAsyncRunOnce = (
  fn: () => Promise<void>,
  condition: boolean
) => {
  const [hasBeenCalled, setHasBeenCalled] = useState(false)
  const [hasBeenAwaited, setHasBeenAwaited] = useState(false)

  useEffect(() => {
    if (condition && !hasBeenCalled) {
      const executeFn = async () => {
        try {
          await fn()
        } catch (error) {
          console.error('Error executing async function:', error)
        }
        setHasBeenAwaited(true)
      }
      setHasBeenCalled(true)
      void executeFn()
    }
  }, [condition, fn, hasBeenCalled])

  return {
    hasBeenCalled,
    hasBeenAwaited,
  }
}
